import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useLanguage } from "../Context/LanguageContext";
import {
    SITE_URL,
    SITE_NAME,
    DEFAULT_OG_IMAGE,
    OG_LOCALE,
    HTML_LANG,
    resolveMeta,
} from "../seo/seoConfig";

// ---------------------------------------------------------------------------
// <SeoManager/>
// ---------------------------------------------------------------------------
// Renders nothing. On every navigation and language change it imperatively
// upserts the document <head> tags (title, description, robots, canonical,
// Open Graph, Twitter) and the <html lang> attribute.
//
// We manage the tags imperatively (rather than via React 19 metadata hoisting)
// so we update the SAME tags that already exist statically in public/index.html
// instead of appending duplicates. Every tag we own is marked data-seo so it is
// easy to identify and never collides with third-party tags.
// ---------------------------------------------------------------------------

/** Upsert a <meta> tag identified by an attribute (name or property). */
function setMeta(attr, key, content) {
    if (content == null) {
        const existing = document.head.querySelector(`meta[${attr}="${key}"]`);
        if (existing && existing.getAttribute("data-seo") === "true") existing.remove();
        return;
    }
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute("data-seo", "true");
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

/** Upsert a <link> tag identified by its rel. */
function setLink(rel, href) {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        el.setAttribute("data-seo", "true");
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

const SeoManager = () => {
    const { pathname } = useLocation();
    const { language } = useLanguage();

    useEffect(() => {
        const lang = HTML_LANG[language] ? language : "de";
        const meta = resolveMeta(lang, pathname);
        const canonical = `${SITE_URL}${pathname === "/" ? "" : pathname.replace(/\/$/, "")}` || SITE_URL;

        // <html lang> — important for screen readers and language detection.
        document.documentElement.lang = HTML_LANG[lang];

        // Primary meta.
        document.title = meta.title;
        setMeta("name", "description", meta.description);
        setMeta("name", "keywords", meta.keywords);

        // Indexing: keep brand/content pages indexable, exclude unknown/404 routes.
        setMeta(
            "name",
            "robots",
            meta.isKnown
                ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
                : "noindex, follow"
        );

        // Canonical.
        setLink("canonical", canonical);

        // Open Graph.
        setMeta("property", "og:type", "website");
        setMeta("property", "og:site_name", SITE_NAME);
        setMeta("property", "og:title", meta.title);
        setMeta("property", "og:description", meta.description);
        setMeta("property", "og:url", canonical);
        setMeta("property", "og:image", DEFAULT_OG_IMAGE);
        setMeta("property", "og:image:alt", SITE_NAME);
        setMeta("property", "og:locale", OG_LOCALE[lang]);

        // Twitter.
        setMeta("name", "twitter:card", "summary_large_image");
        setMeta("name", "twitter:title", meta.title);
        setMeta("name", "twitter:description", meta.description);
        setMeta("name", "twitter:image", DEFAULT_OG_IMAGE);
    }, [pathname, language]);

    return null;
};

export default SeoManager;
